import React from 'react'
import * as Request from '../../../utilities/request'
import { IpApiResponse, countryToPricingTable } from '../../../utilities/pricingTables'
import { AuthState } from '../../../App.d'

interface PricingCardProps {
	title: string
	description: string
	product: 'subsync' | 'leavecal'
	usageLabel: string
	orgCurrency: string
	minValue: number
	maxValue: number
	defaultValue: number
	authState: AuthState
	onCheckoutError: (message: string) => void
}

export const PricingCard = (props: PricingCardProps) => {
	const [currencySymbol, setCurrencySymbol] = React.useState<string>('$')
	const [sliderValue, setSliderValue] = React.useState<number>(props.defaultValue)
	const [calculatedCost, setCalculatedCost] = React.useState<number | null>(null)
	const [isLoadingCost, setIsLoadingCost] = React.useState<boolean>(false)
	const [isCheckingOut, setIsCheckingOut] = React.useState<boolean>(false)

	// Cost calculation with debounce
	React.useEffect(() => {
		const timer = setTimeout(async () => {
			setIsLoadingCost(true)
			const req = await Request.get(`webhook/subscription/${props.product}/${props.orgCurrency}/${sliderValue}`, props.authState)
			if (req.data) {
				setCalculatedCost(req.data.cost)
				setCurrencySymbol(req.data.symbol)
			}
			setIsLoadingCost(false)
		}, 200)
		return () => clearTimeout(timer)
	}, [sliderValue, props.orgCurrency, props.authState, props.product])

	const handleGetStarted = async () => {
		setIsCheckingOut(true)
		const req = await Request.post(
			'subscription/checkout-redirect',
			{
				successUrl: `${window.location.origin}/#billing?success=true`,
				cancelUrl: `${window.location.origin}/#billing`,
				currency: props.orgCurrency,
			},
			props.authState
		)

		if (req.data?.checkoutUrl) {
			window.location.href = req.data.checkoutUrl
		} else {
			props.onCheckoutError('Failed to start checkout. Please try again.')
			setIsCheckingOut(false)
		}
	}

	// Update slider progress for visual fill
	const sliderProgress = ((sliderValue - props.minValue) / (props.maxValue - props.minValue)) * 100

	return (
		<div style={styles.pricingCard}>
			<h4 style={styles.pricingTitle}>{props.title}</h4>
			<p style={styles.pricingDescription}>{props.description}</p>

			<div style={styles.sliderLabel}>
				{sliderValue} {props.usageLabel}
			</div>
			<input
				type="range"
				min={props.minValue}
				max={props.maxValue}
				value={sliderValue}
				onChange={(e) => setSliderValue(Number(e.target.value))}
				style={{
					...styles.slider,
					background: `linear-gradient(to right, #1a1a5c ${sliderProgress}%, #e0e0e0 ${sliderProgress}%)`,
				}}
			/>

			<div style={styles.priceDisplay}>
				{isLoadingCost ? (
					<span style={styles.loader}>...</span>
				) : (
					<>
						{currencySymbol}
						{calculatedCost?.toFixed(2)}
						<span style={styles.pricePeriod}>/month</span>
					</>
				)}
			</div>

			<button onClick={handleGetStarted} disabled={isCheckingOut} style={styles.getStartedBtn}>
				{isCheckingOut ? 'Loading...' : 'Get Started'}
			</button>
		</div>
	)
}

const styles: Record<string, React.CSSProperties> = {
	pricingCard: {
		maxWidth: '400px',
		margin: '20px auto 0',
		padding: '32px',
		textAlign: 'center',
		background: '#fff',
		borderRadius: '8px',
		border: '1px solid #e0e0e0',
	},
	pricingTitle: {
		fontSize: '20px',
		fontWeight: 700,
		color: '#1a1a5c',
		marginBottom: '8px',
	},
	pricingDescription: {
		fontSize: '14px',
		color: '#666',
		marginBottom: '24px',
	},
	sliderLabel: {
		fontSize: '16px',
		fontWeight: 600,
		color: '#333',
		marginBottom: '8px',
	},
	slider: {
		WebkitAppearance: 'none',
		appearance: 'none',
		width: '100%',
		height: '8px',
		borderRadius: '4px',
		outline: 'none',
		cursor: 'pointer',
	},
	priceDisplay: {
		fontSize: '32px',
		fontWeight: 700,
		color: '#1a1a5c',
		margin: '24px 0',
	},
	pricePeriod: {
		fontSize: '16px',
		fontWeight: 400,
		color: '#666',
	},
	loader: {
		color: '#999',
	},
	getStartedBtn: {
		width: '100%',
		padding: '12px 24px',
		background: '#1a1a5c',
		color: '#fff',
		border: 'none',
		borderRadius: '24px',
		fontWeight: 600,
		cursor: 'pointer',
		fontSize: '16px',
	},
}
